import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ToastHost.css";

const TOAST_DURATION = 3500;

const getToastType = (message) => {
  const value = String(message).toLowerCase();

  if (
    value.includes("fail") ||
    value.includes("error") ||
    value.includes("unable") ||
    value.includes("wrong") ||
    value.includes("not reachable")
  ) {
    return "error";
  }

  if (
    value.includes("required") ||
    value.includes("cannot") ||
    value.includes("must") ||
    value.includes("select") ||
    value.includes("exceed")
  ) {
    return "warning";
  }

  if (
    value.includes("success") ||
    value.includes("saved") ||
    value.includes("updated") ||
    value.includes("deleted")
  ) {
    return "success";
  }

  return "info";
};

const ToastHost = () => {
  const [toasts, setToasts] = useState([]);
  const confirmations = useRef(new Map());

  useEffect(() => {
    const originalAlert = window.alert;
    const pendingConfirmations = confirmations.current;

    const addToast = (message) => {
      const id = `${Date.now()}-${Math.random()}`;
      const toast = {
        id,
        message: String(message),
        type: getToastType(message)
      };

      setToasts((current) => [...current, toast]);

      window.setTimeout(() => {
        setToasts((current) => current.filter((item) => item.id !== id));
      }, TOAST_DURATION);
    };
    const onToast = (event) => addToast(event.detail);
    const onConfirm = (event) => {
      const id = `${Date.now()}-${Math.random()}`;
      pendingConfirmations.set(id, event.detail.resolve);
      setToasts(current => [...current, {
        id, message: event.detail.message, type: "warning", confirmation: true
      }]);
    };
    window.addEventListener("app:confirm-toast", onConfirm);
    window.addEventListener("app:toast", onToast);
    window.alert = addToast;

    return () => {
      window.alert = originalAlert;
      window.removeEventListener("app:toast", onToast);
      window.removeEventListener("app:confirm-toast", onConfirm);
      pendingConfirmations.forEach(resolve => resolve(false));
      pendingConfirmations.clear();
    };
  }, []);

  const dismiss = (id, accepted = false) => {
    const resolve = confirmations.current.get(id);
    confirmations.current.delete(id);
    if (resolve) resolve(accepted);
    setToasts((current) => current.filter((item) => item.id !== id));
  };

  return createPortal(
    <div className="toast-host" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`app-toast app-toast-${toast.type}`}
          role={toast.type === "error" ? "alert" : "status"}
        >
          <div>
            <span>{toast.message}</span>
            {toast.confirmation && (
              <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-danger btn-sm" onClick={() => dismiss(toast.id, true)}>Delete</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => dismiss(toast.id)}>Cancel</button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="app-toast-close"
            aria-label="Dismiss notification"
            onClick={() => dismiss(toast.id)}
          >
            &times;
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default ToastHost;
